import ResetPassword from "./ResetPassword";
import VerifyOtpPage from "../../signInRegisterPages/VerifyOtpPage/VerifyOtpPage";
import CreatePasswordPage from "../CreatePasswordPage/CreatePasswordPage";
import { startTransition, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/common/Header/Header";
import { toast } from "sonner";
import useScreenResolution from "../../../hooks/useScreenResolution";
import backArrow from "../../../assets/images/greenbackarrow.svg";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import { useTranslation } from "react-i18next";

const PageState = {
  OTP_VERIFIED: "OTP_VERIFIED",
  PASSWORD_CREATED: "PASSWORD_CREATED",
  RESET_PASSWORD: "RESET_PASSWORD",
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const translate = useLanguageTranslate();
  const { mobile } = useScreenResolution();
  const [currentPage, setCurrentPage] = useState(PageState.RESET_PASSWORD);
  // Which way the next step enters: set just before the step changes, read
  // when it renders. Continue slides in from the reading end, Back from the
  // start (index.css, [data-auth-step]).
  const navDirection = useRef("forward");

  const handleSuccess = (nextPage) => () => {
    navDirection.current = "forward";
    startTransition(() => {
      setCurrentPage(nextPage);
    });
  };

  const handleBackClicked = () => {
    navDirection.current = "back";
    startTransition(() => {
      switch (currentPage) {
        case PageState.RESET_PASSWORD:
          navigate("/");
          break;
        case PageState.PASSWORD_CREATED:
          setCurrentPage(PageState.OTP_VERIFIED);
          break;
        case PageState.OTP_VERIFIED:
          setCurrentPage(PageState.RESET_PASSWORD);
          break;
        default:
          break;
      }
    });
  };
  const redirectToSignIn = () => {
    toast.success("Password updated successfully");
    navigate("/sign-in");
  };

  const PageRenderer = () => {
    switch (currentPage) {
      case PageState.RESET_PASSWORD:
        return (
          <ResetPassword onSuccess={handleSuccess(PageState.OTP_VERIFIED)} />
        );
      case PageState.OTP_VERIFIED:
        return (
          <VerifyOtpPage
            pinActionOnMobile
            pageTitle={translate("Verify OTP", "التحقق من الرمز")}
            onSuccess={handleSuccess(PageState.PASSWORD_CREATED)}
            onBackClick={handleBackClicked}
            titleDesc={
              mobile
              ? t("forgot_password_flow.verify_otp_page.title_desc_mobile")
              : t("forgot_password_flow.verify_otp_page.title_desc_code")
            }
          />
        );
      case PageState.PASSWORD_CREATED:
        return (
          <CreatePasswordPage
            pinActionOnMobile
            onBackClick={handleBackClicked}
            pageTitle={t("forgot_password_flow.create_password_page.page_title")}
            onSuccess={redirectToSignIn}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Header
        showBackBtn={mobile}
        isPopup
        onCloseModal={() => {
          startTransition(() => {
            navigate("/");
          });
        }}
      />

      {/* Back steps through the flow (OTP -> CPR, password -> OTP), so it is
          the flow's own button rather than the sign-up one. Pinned in the
          header row opposite Close, like the other journeys: it used to sit
          in the page at 80px, over the first step's title. Portalled so the
          page fade's stacking context cannot hold it under the header. */}
      {mobile &&
        createPortal(
          <button
            type="button"
            aria-label={translate("Back", "رجوع")}
            className="fixed top-[19px] left-4 rtl:left-auto rtl:right-4 z-30 flex w-10 h-10 items-center justify-center rounded-full transition-transform duration-150 ease-out active:scale-[0.94]"
            onClick={handleBackClicked}
          >
            <img src={backArrow} alt="" className="w-10 h-10 rtl:rotate-180" />
          </button>,
          document.body
        )}
      <div className="mainSignupModal relative">
        {/* Keyed by step so each one mounts fresh and plays its entrance. */}
        <div
          key={currentPage}
          data-auth-step
          data-auth-nav={navDirection.current}
        >
          {PageRenderer()}
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
